// components/onboarding/TrainerOnboardingTooltip.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTrainerOnboarding } from '../../context/TrainerOnboardingContext';
import { FiArrowRight, FiArrowLeft, FiX, FiCheck } from 'react-icons/fi';
import '../../styles/Onboarding.css';

// Mobile breakpoint - matches typical mobile sidebar breakpoint
const MOBILE_BREAKPOINT = 768;

/**
 * TrainerOnboardingTooltip
 *
 * Positioned tooltip that highlights elements and shows step instructions.
 * Handles both desktop (sidebar visible) and mobile (hamburger menu) layouts.
 */
const TrainerOnboardingTooltip = () => {
    const {
        isOnboardingActive,
        currentStep,
        getCurrentStepConfig,
        nextStep,
        prevStep,
        skipOnboarding,
        isLastStep,
        isFirstTourStep,
        totalSteps,
    } = useTrainerOnboarding();

    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [targetRect, setTargetRect] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const tooltipRef = useRef(null);

    const stepConfig = getCurrentStepConfig();

    /**
     * Check if mobile and update state
     */
    const checkMobile = useCallback(() => {
        setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    }, []);

    /**
     * Open mobile sidebar for onboarding
     */
    const openMobileSidebar = useCallback(() => {
        const mobileSidebar = document.querySelector('.mobile-sidebar');
        if (mobileSidebar && !mobileSidebar.classList.contains('open')) {
            mobileSidebar.classList.add('open');

            // Create backdrop if it doesn't exist
            let backdrop = document.querySelector('.sidebar-backdrop');
            if (!backdrop) {
                backdrop = document.createElement('div');
                backdrop.className = 'sidebar-backdrop';
                backdrop.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 999;';
                document.body.appendChild(backdrop);
            }

            setIsMobileSidebarOpen(true);
        }
    }, []);

    /**
     * Close mobile sidebar
     */
    const closeMobileSidebar = useCallback(() => {
        const mobileSidebar = document.querySelector('.mobile-sidebar');
        const backdrop = document.querySelector('.sidebar-backdrop');

        if (mobileSidebar) {
            mobileSidebar.classList.remove('open');
        }
        if (backdrop) {
            backdrop.remove();
        }
        setIsMobileSidebarOpen(false);
    }, []);

    /**
     * Calculate tooltip position based on target element
     */
    const calculatePosition = useCallback(() => {
        if (!stepConfig?.targetSelector) return;

        // On mobile, look for the element in the mobile sidebar first
        let targetElement = null;
        if (isMobile) {
            const mobileSidebar = document.querySelector('.mobile-sidebar');
            if (mobileSidebar) {
                targetElement = mobileSidebar.querySelector(stepConfig.targetSelector);
            }
        }

        // Fall back to document-level query
        if (!targetElement) {
            targetElement = document.querySelector(stepConfig.targetSelector);
        }

        if (!targetElement) return;

        const rect = targetElement.getBoundingClientRect();
        setTargetRect(rect);

        const tooltipEl = tooltipRef.current;
        const tooltipHeight = tooltipEl?.offsetHeight || 200;
        const tooltipWidth = tooltipEl?.offsetWidth || 320;

        if (isMobile) {
            // On mobile, position tooltip at the bottom of the screen
            // This ensures it never covers the sidebar menu items
            const left = (window.innerWidth - tooltipWidth) / 2;
            // Fixed position near bottom of screen
            const top = window.innerHeight - tooltipHeight - 30;
            setPosition({
                top: Math.max(top, rect.bottom + 20), // At least 20px below element
                left: Math.max(left, 10)
            });
        } else {
            // Desktop: Position to the right of the target element
            let left = rect.right + 20;
            let top = rect.top + (rect.height / 2) - (tooltipHeight / 2);

            // Check if tooltip would go off-screen to the right
            if (left + tooltipWidth > window.innerWidth - 20) {
                // Position to the left of target instead
                left = rect.left - tooltipWidth - 20;
            }

            // Ensure tooltip doesn't go off-screen vertically
            if (top < 20) {
                top = 20;
            } else if (top + tooltipHeight > window.innerHeight - 20) {
                top = window.innerHeight - tooltipHeight - 20;
            }

            // Ensure tooltip doesn't go off-screen to the left
            if (left < 20) {
                left = 20;
            }

            setPosition({ top, left });
        }
    }, [stepConfig, isMobile]);

    // Handle window resize
    useEffect(() => {
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [checkMobile]);

    // On mobile, open sidebar when showing menu-related steps
    useEffect(() => {
        if (!isOnboardingActive || currentStep === 0) return;

        if (isMobile && stepConfig?.targetSelector) {
            // Open mobile sidebar to show the target element
            openMobileSidebar();

            // Recalculate position after sidebar opens
            const timer = setTimeout(calculatePosition, 300);
            return () => clearTimeout(timer);
        }
    }, [isOnboardingActive, currentStep, isMobile, stepConfig, openMobileSidebar, calculatePosition]);

    // Recalculate position when step changes or window resizes
    useEffect(() => {
        if (!isOnboardingActive || !stepConfig?.targetSelector) return;

        // Initial calculation
        calculatePosition();

        // Recalculate on resize
        window.addEventListener('resize', calculatePosition);
        window.addEventListener('scroll', calculatePosition);

        // Use MutationObserver to detect DOM changes
        const observer = new MutationObserver(calculatePosition);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
        });

        return () => {
            window.removeEventListener('resize', calculatePosition);
            window.removeEventListener('scroll', calculatePosition);
            observer.disconnect();
        };
    }, [isOnboardingActive, currentStep, stepConfig, calculatePosition]);

    // Re-calculate after a short delay to ensure DOM is ready
    useEffect(() => {
        if (isOnboardingActive) {
            const timer = setTimeout(calculatePosition, 100);
            return () => clearTimeout(timer);
        }
    }, [isOnboardingActive, currentStep, calculatePosition]);

    // Cleanup mobile sidebar when onboarding ends
    useEffect(() => {
        if (!isOnboardingActive && isMobileSidebarOpen) {
            closeMobileSidebar();
        }
    }, [isOnboardingActive, isMobileSidebarOpen, closeMobileSidebar]);

    if (!isOnboardingActive || !stepConfig || currentStep === 0) {
        return null;
    }

    // Calculate step number (1-indexed, excluding welcome step)
    const stepNumber = currentStep;

    return (
        <div
            ref={tooltipRef}
            className={`onboarding-tooltip ${isMobile ? 'onboarding-tooltip--mobile' : ''}`}
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
            }}
        >
            {/* Arrow pointing to target (desktop only) */}
            {!isMobile && targetRect && position.left > targetRect.right && (
                <div className="onboarding-tooltip-arrow onboarding-tooltip-arrow-left" />
            )}
            {!isMobile && targetRect && position.left < targetRect.left && (
                <div className="onboarding-tooltip-arrow onboarding-tooltip-arrow-right" />
            )}

            {/* Close button */}
            <button
                className="onboarding-tooltip-close"
                onClick={skipOnboarding}
                title="Zamknij przewodnik"
                aria-label="Zamknij przewodnik"
            >
                <FiX />
            </button>

            {/* Step indicator */}
            <div className="onboarding-tooltip-progress">
                <span>Krok {stepNumber} z {totalSteps}</span>
                <div className="onboarding-tooltip-dots">
                    {Array.from({ length: totalSteps }, (_, i) => (
                        <span
                            key={i}
                            className={`onboarding-tooltip-dot ${i + 1 === stepNumber ? 'active' : ''} ${i + 1 < stepNumber ? 'completed' : ''}`}
                        />
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="onboarding-tooltip-content">
                <h3>{stepConfig.title}</h3>
                <p>{stepConfig.description}</p>
                {isMobile && (
                    <p className="onboarding-tooltip-mobile-hint">
                        Znajdziesz to w menu bocznym
                    </p>
                )}
            </div>

            {/* Navigation */}
            <div className="onboarding-tooltip-actions">
                {!isFirstTourStep && (
                    <button
                        className="onboarding-btn onboarding-btn-outline"
                        onClick={prevStep}
                    >
                        <FiArrowLeft />
                        Wstecz
                    </button>
                )}

                <button
                    className="onboarding-btn onboarding-btn-primary"
                    onClick={nextStep}
                >
                    {isLastStep ? (
                        <>
                            Zakończ
                            <FiCheck />
                        </>
                    ) : (
                        <>
                            Dalej
                            <FiArrowRight />
                        </>
                    )}
                </button>
            </div>

            {/* Skip option */}
            <button
                className="onboarding-tooltip-skip"
                onClick={skipOnboarding}
            >
                Pomiń przewodnik
            </button>
        </div>
    );
};

export default TrainerOnboardingTooltip;
